const UUID_SEGMENT_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const NUMERIC_SEGMENT_RE = /^\d+$/
const LONG_HEX_SEGMENT_RE = /^[0-9a-f]{24,}$/i

const SENSITIVE_KEY_RE =
  /(email|phone|name|search|query|message|note|notes|transcript|resume|token|secret|password)/i

const SAFE_QUERY_KEY_LABELS = new Set([
  "activity",
  "admin",
  "agency-ops",
  "all",
  "application",
  "assessment-interviewers",
  "assessment-sessions",
  "assessments",
  "bookings",
  "by-candidate",
  "candidate_score",
  "candidates",
  "client-inbox",
  "conversations",
  "count",
  "current",
  "default",
  "detail",
  "handoff",
  "handoffs",
  "me",
  "messaging",
  "newCount",
  "newHiresCount",
  "org",
  "personalized",
  "pipeline",
  "recruiters",
  "reports",
  "scored",
  "stages",
  "stats",
  "teams",
  "templates",
  "transcript",
  "users",
])

type FailureClass =
  | "aborted"
  | "offline"
  | "network"
  | "authentication"
  | "permission"
  | "stale_reference"
  | "conflict"
  | "validation"
  | "rate_limited"
  | "server"
  | "unknown"

function pathWithoutQuery(rawPath?: string | null): string | null {
  if (!rawPath) return null
  try {
    return new URL(rawPath, "https://app.local").pathname
  } catch {
    return rawPath.split(/[?#]/)[0] || null
  }
}

export function normalizePathPattern(rawPath?: string | null): string | null {
  const pathname = pathWithoutQuery(rawPath)
  if (!pathname) return null

  const segments = pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => {
      if (UUID_SEGMENT_RE.test(segment)) return ":uuid"
      if (NUMERIC_SEGMENT_RE.test(segment)) return ":id"
      if (LONG_HEX_SEGMENT_RE.test(segment)) return ":hash"
      return segment
    })

  return `/${segments.join("/")}`
}

export function currentRouteSurface(): string | null {
  if (typeof window === "undefined") return null
  return normalizePathPattern(window.location.pathname)
}

function sanitizeMessage(message: string): string {
  return message
    .replace(/\/api\/v1\/[^\]\s)]+/g, (match) => normalizePathPattern(match) ?? match)
    .replace(/https?:\/\/[^\]\s)]+/g, (match) => normalizePathPattern(match) ?? "[url]")
}

function errorRecord(error: unknown) {
  return error && typeof error === "object"
    ? (error as {
        name?: unknown
        message?: unknown
        status?: unknown
        response?: { status?: unknown }
        path?: unknown
      })
    : null
}

export function errorTelemetry(error: unknown) {
  const record = errorRecord(error)
  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof record?.message === "string"
      ? record.message
      : typeof error === "string"
      ? error
      : String(error)
  const status =
    typeof record?.status === "number"
      ? record.status
      : typeof record?.response?.status === "number"
      ? record.response.status
      : null
  const apiPath = typeof record?.path === "string" ? record.path : null
  const name =
    typeof record?.name === "string"
      ? record.name
      : error instanceof Error
      ? error.name
      : "Error"

  const isAbort =
    name === "AbortError" || /abort|aborted|signal is aborted/i.test(rawMessage)
  const isNetwork =
    status == null &&
    (name === "ApiNetworkError" ||
      name === "TypeError" ||
      /failed to fetch|load failed|network|connection/i.test(rawMessage))
  const isOffline =
    typeof navigator !== "undefined" && navigator.onLine === false
  const failureClass: FailureClass = isAbort
    ? "aborted"
    : isOffline
    ? "offline"
    : isNetwork
    ? "network"
    : status === 401
    ? "authentication"
    : status === 403
    ? "permission"
    : status === 404 || status === 410
    ? "stale_reference"
    : status === 409
    ? "conflict"
    : status === 400 || status === 422
    ? "validation"
    : status === 408 || status === 429
    ? "rate_limited"
    : status != null && status >= 500
    ? "server"
    : "unknown"

  return {
    error_name: name,
    message: sanitizeMessage(rawMessage),
    status,
    api_path: normalizePathPattern(apiPath),
    failure_class: failureClass,
    expected_failure: [
      "authentication",
      "permission",
      "stale_reference",
      "conflict",
      "validation",
      "offline",
    ].includes(failureClass),
    is_abort: isAbort,
    is_offline: isOffline,
  }
}

function safeScalar(value: unknown): unknown {
  if (typeof value === "number") return ":id"
  if (typeof value === "boolean" || value === null) return value
  if (typeof value !== "string") return typeof value
  if (!value) return ""
  if (UUID_SEGMENT_RE.test(value) || NUMERIC_SEGMENT_RE.test(value)) return ":id"
  if (value.includes("@") || value.length > 32 || /\s/.test(value)) return "[set]"
  return value
}

function safeObjectShape(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, item]) => {
        if (SENSITIVE_KEY_RE.test(key)) return [key, item ? "[set]" : ""]
        if (/_id$|^id$/i.test(key)) return [key, item == null ? null : ":id"]
        if (Array.isArray(item)) return [key, `array:${item.length}`]
        if (item && typeof item === "object") return [key, "object"]
        return [key, safeScalar(item)]
      })
  )
}

export function queryKeyTelemetry(queryKey: unknown) {
  const parts = Array.isArray(queryKey) ? queryKey : [queryKey]
  const family = typeof parts[0] === "string" ? parts[0] : typeof parts[0]
  const safeParts = parts.map((part, index) => {
    if (part && typeof part === "object" && !Array.isArray(part)) {
      return safeObjectShape(part as Record<string, unknown>)
    }
    if (
      index > 0 &&
      typeof part === "string" &&
      !SAFE_QUERY_KEY_LABELS.has(part) &&
      !UUID_SEGMENT_RE.test(part) &&
      !NUMERIC_SEGMENT_RE.test(part)
    ) {
      return part ? "[set]" : ""
    }
    return safeScalar(part)
  })

  return {
    query_key_family: family,
    query_key_shape: JSON.stringify(safeParts),
    query_key_depth: parts.length,
  }
}

export function mutationKeyTelemetry(
  mutationKey: unknown,
  apiPath?: string | null
) {
  const queryTelemetry = queryKeyTelemetry(
    mutationKey ?? (apiPath ? ["api", apiPath] : ["unkeyed"])
  )

  return {
    mutation_key_family: queryTelemetry.query_key_family,
    mutation_key_shape: queryTelemetry.query_key_shape,
    mutation_key_depth: queryTelemetry.query_key_depth,
  }
}
